// frontend/src/components/production-sequence/InlineEditCell.tsx
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  dark: boolean;
  value: string;
  pending: boolean;
  multiline?: boolean;
  onSave: (value: string) => void;
}

export function InlineEditCell({ dark, value, pending, multiline = false, onSave }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);
  useEffect(() => { setDraft(value); }, [value]);

  function commit() { setEditing(false); if (draft !== value) onSave(draft); }
  function cancel() { setEditing(false); setDraft(value); }

  if (editing) {
    const cls = `w-full text-[12px] px-1.5 py-1 rounded outline-none ring-1 ring-sky-400 ${
      dark ? 'bg-slate-800 text-slate-100' : 'bg-white text-slate-800'
    }`;
    return multiline ? (
      <textarea
        ref={ref} value={draft} rows={2} className={cls}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
          if (e.key === 'Escape') cancel();
        }}
      />
    ) : (
      <input
        ref={ref} value={draft} className={cls}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
      />
    );
  }

  const muted = dark ? 'text-slate-600' : 'text-slate-400';
  return (
    <button
      onClick={() => setEditing(true)}
      className={`group flex items-center gap-1 w-full text-left text-[12px] truncate cursor-text rounded px-1 py-0.5 ${
        dark ? 'hover:bg-white/5' : 'hover:bg-slate-100'
      }`}
      title={value || undefined}
    >
      <span className={value ? '' : muted}>{value || t('sequence.inline.placeholder')}</span>
      {pending && (
        <svg className="w-3 h-3 shrink-0 text-sky-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-label={t('sequence.inline.pendingSync')}>
          <path d="M3.5 7a4.5 4.5 0 018.5-1.5M12.5 9a4.5 4.5 0 01-8.5 1.5" />
          <path d="M12 3v2.5H9.5M4 13v-2.5H6.5" />
        </svg>
      )}
    </button>
  );
}
